// ── Tenant currency for the appointments app ─────────────────────────────
//
// Every price and revenue figure here used to be `toFixed(2)`. On a KWD
// tenant (3 decimals) that turns a 0.136 service into "0.14" — and the totals
// this app shows are the same numbers the cashier and the receipt show, so
// they have to agree.
//
// The value comes from `GET /api/v1/tenant/currency` (JWT-scoped to the
// session's subsidiary), warmed once by the app shell. It is mirrored into a
// plain module store so non-React code can format too, and so the formatter
// stays a plain function — the alternative is threading a currency prop
// through every dialog, table row and CSV export.

import type { Currency } from '@matix/types';
import { FALLBACK_CURRENCY } from '@matix/types';
import { roundMoney, safePrecision } from '@matix/utils';

let current: Currency = FALLBACK_CURRENCY;

/** Called by the app shell as soon as the currency query resolves. */
export function setCurrency(c: Currency | null | undefined): void {
  if (c) current = c;
}

/** Decimals to format and round money at. 2 until the query lands. */
export function moneyPrecision(): number {
  return safePrecision(current.precision);
}

/** ISO-like code (e.g. "KWD") for the few places that show it. */
export function currencyCode(): string {
  return current.code;
}

/** Round money the same way the cashier and the server do. */
export function roundM(n: number): number {
  return roundMoney(n, current.precision);
}

/**
 * Format money for display at the tenant currency's precision.
 * Plain digits, no code — the surrounding label carries the currency.
 */
export function fmtMoney(n: number | null | undefined): string {
  const digits = moneyPrecision();
  return (Number.isFinite(n) ? Number(n) : 0).toFixed(digits);
}
